import React from "react";

export default function HowFacilitiesGetProjects() {
  const steps = [
    {
      number: "01",
      title: "Register Your Facility",
      desc: "Create your facility profile and submit verification documents.",
    },
    {
      number: "02",
      title: "List Your Capabilities",
      desc: "Add machines, technologies, processes, materials and services.",
    },
    {
      number: "03",
      title: "Get Verified",
      desc: "APIC Admin reviews your facility and publishes your profile.",
    },
    {
      number: "04",
      title: "Innovators Submit RFQs",
      desc: "Prototype requirements, CAD files, drawings and specifications arrive.",
    },
    {
      number: "05",
      title: "Receive Relevant RFQs",
      desc: "Only RFQs matching your capabilities and capacity.",
    },
    {
      number: "06",
      title: "Review Requirements",
      desc: "Download CAD, review specs and understand the project.",
    },
    {
      number: "07",
      title: "Submit Your Quotation",
      desc: "Cost, timeline, materials, manufacturing, taxes and T&C.",
    },
    {
      number: "08",
      title: "Innovator Compares Quotations",
      desc: "By price, delivery, capability, rating and experience.",
    },
    {
      number: "09",
      title: "Receive Work Order",
      desc: "The selected facility receives the confirmed work order.",
    },
    {
      number: "10",
      title: "Execute & Deliver",
      desc: "Manage the complete project digitally to delivery.",
    },
  ];

  return (
    <>
      <ResponsiveStyles />
      <section className="hfp-section" style={styles.section}>
        <div className="hfp-grid-overlay" style={styles.gridOverlay} />
        <div className="hfp-wrap" style={styles.wrap}>
          <div className="hfp-header" style={styles.header}>
            <span className="hfp-eyebrow" style={styles.eyebrow}>
              <span style={styles.eyebrowDot} />
              HOW IT WORKS
            </span>

            <h2 className="hfp-heading" style={styles.heading}>
              How do facilities get{" "}
              <span style={styles.accent}>projects?</span>
            </h2>

            <p className="hfp-sub" style={styles.subheading}>
              A structured, transparent process from registration to delivery.
            </p>
          </div>

          <div className="hfp-grid" style={styles.grid}>
            {steps.map((s, i) => (
              <div className="hfp-card" style={styles.card} key={i}>
                <div className="hfp-card-top" style={styles.cardTop}>
                  <span className="hfp-number" style={styles.number}>
                    {s.number}
                  </span>
                  <svg
                    className="hfp-arrow"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8a93b8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </div>
                <h3 className="hfp-card-title" style={styles.cardTitle}>
                  {s.title}
                </h3>
                <p className="hfp-card-desc" style={styles.cardDesc}>
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Responsive rules: 5 columns on desktop, 3 on small laptops,
 * 2 on tablets, and a single column stack (flex column) on mobile
 * so cards never fight the grid for width on narrow screens.
 */
function ResponsiveStyles() {
  return (
    <style>{`
      @media (max-width: 1280px) {
        .hfp-grid {
          grid-template-columns: repeat(3, 1fr) !important;
        }
      }

      @media (max-width: 900px) {
        .hfp-grid {
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 16px !important;
        }
        .hfp-heading {
          font-size: clamp(1.9rem, 5vw, 2.6rem) !important;
        }
      }

      @media (max-width: 640px) {
        .hfp-section {
          padding: 48px 0 !important;
          min-height: auto !important;
        }
        .hfp-wrap {
          padding: 0 18px !important;
        }
        .hfp-eyebrow {
          font-size: 10.5px !important;
          padding: 7px 14px !important;
        }
        .hfp-heading {
          font-size: clamp(1.6rem, 7vw, 2.1rem) !important;
          line-height: 1.2 !important;
        }
        .hfp-sub {
          font-size: 0.96rem !important;
          padding: 0 8px !important;
        }
        .hfp-grid {
          display: flex !important;
          flex-direction: column !important;
          grid-template-columns: none !important;
          gap: 14px !important;
          margin-top: 32px !important;
        }
        .hfp-card {
          width: 100% !important;
          box-sizing: border-box !important;
          padding: 22px 20px !important;
        }
        .hfp-number {
          font-size: 1.5rem !important;
        }
        .hfp-card-title {
          font-size: 1.02rem !important;
        }
        .hfp-card-desc {
          font-size: 0.9rem !important;
        }
      }

      @media (max-width: 380px) {
        .hfp-heading {
          font-size: 1.4rem !important;
        }
      }
    `}</style>
  );
}

const styles = {
  section: {
    position: "relative",
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    background:
      "radial-gradient(1000px 600px at 50% -10%, rgba(247,148,29,0.08), transparent 60%), linear-gradient(180deg, #070c1f 0%, #0a1130 100%)",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    boxSizing: "border-box",
    padding: "30px 0",
    overflow: "hidden",
  },
  gridOverlay: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
    backgroundSize: "44px 44px",
    pointerEvents: "none",
  },
  wrap: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 1320,
    margin: "0 auto",
    padding: "0 32px",
    boxSizing: "border-box",
  },
  header: {
    textAlign: "center",
    marginBottom: 56,
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "9px 18px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.04)",
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: "#e7ebff",
  },
  eyebrowDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#f7941d",
    flexShrink: 0,
  },
  heading: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 800,
    fontSize: "clamp(2.1rem, 4vw, 3.2rem)",
    letterSpacing: "-0.02em",
    color: "#ffffff",
    margin: "22px 0 0",
    lineHeight: 1.15,
  },
  accent: {
    color: "#f7941d",
  },
  subheading: {
    marginTop: 16,
    fontSize: "1.08rem",
    lineHeight: 1.7,
    color: "#b7c0dd",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 20,
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: "26px 24px",
    boxSizing: "border-box",
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  number: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 800,
    fontSize: "1.9rem",
    color: "#f7941d",
    letterSpacing: "-0.02em",
  },
  cardTitle: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 700,
    fontSize: "1.08rem",
    color: "#ffffff",
    margin: "16px 0 0",
    lineHeight: 1.3,
  },
  cardDesc: {
    marginTop: 10,
    fontSize: "0.92rem",
    lineHeight: 1.6,
    color: "#9aa2c4",
  },
};